import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ListChecks, Plus, Trash2 } from 'lucide-react';
import {
  DEFAULT_FIELD_CHECKLIST,
  type FieldChecklistKind,
  type FieldSettings,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import {
  CHECKLIST_KIND_LABELS,
  addDraftRow,
  checklistDraftProblem,
  draftRowsFrom,
  relabelDraftRow,
  stepsFromDraft,
  type ChecklistDraftRow,
} from '../../lib/field-checklist';
import { Button, Card, ErrorNotice, Pill, Spinner } from '../../components/ui';

/**
 * The steps a technician ticks at the wall (2026-09-16), and the editor
 * for them.
 *
 * The list is the operator's: "Fibre terminated and cleaned", "Light
 * reading at the ONU" (a dBm figure the phone can take from the live
 * reading), "Customer shown the speed test". A required step left undone
 * is NAMED on the job's requirements; it does not stop a submission — the
 * outcome is still the fix and the photograph. The built-in list stands
 * until the operator writes their own, and the card says which is in force.
 */
export function ChecklistCard() {
  const settings = useQuery({
    queryKey: ['field', 'settings'],
    queryFn: () => api.get<FieldSettings>('/field/settings'),
  });
  const [editing, setEditing] = useState(false);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ListChecks className="size-4 text-muted" />
          Job checklist
        </span>
      }
      action={
        settings.data ? (
          <Button size="sm" onClick={() => setEditing(true)}>
            Edit steps
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-3 p-5">
        {settings.isLoading ? (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        ) : settings.isError ? (
          <ErrorNotice error={settings.error as Error} />
        ) : settings.data ? (
          <>
            <p className="text-sm text-muted">
              What the technician ticks at the wall before submitting. A required step left undone
              is named on the job, beside the outcome — it does not hold the submission.
            </p>
            <ol className="divide-y divide-line rounded-lg border border-line">
              {settings.data.checklist.map((step, i) => (
                <li key={step.key} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <span className="w-5 text-xs text-faint">{i + 1}.</span>
                  <span className="min-w-0 flex-1 text-ink">{step.label}</span>
                  <Pill tone="neutral">{CHECKLIST_KIND_LABELS[step.kind]}</Pill>
                  {step.required && <Pill tone="info">Required</Pill>}
                </li>
              ))}
            </ol>
            {settings.data.checklistIsDefault && (
              <Pill tone="neutral">Using the built-in list</Pill>
            )}
          </>
        ) : null}
      </div>
      {editing && settings.data && (
        <ChecklistEditorDialog
          initial={draftRowsFrom(settings.data.checklist)}
          onClose={() => setEditing(false)}
        />
      )}
    </Card>
  );
}

const KINDS: FieldChecklistKind[] = ['tick', 'light_reading', 'text'];

/**
 * Edit the step list. Closes on Save and runs the write through the tasks
 * tray; the card refreshes when it lands. A fresh row is keyed from its
 * label as it is typed; a saved row keeps its key under a relabel, so the
 * ticks already recorded on open jobs stay attached.
 */
function ChecklistEditorDialog({
  initial,
  onClose,
}: {
  initial: ChecklistDraftRow[];
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [rows, setRows] = useState<ChecklistDraftRow[]>(initial);
  const problem = checklistDraftProblem(rows);

  const update = (index: number, patch: Partial<ChecklistDraftRow>) =>
    setRows((list) => list.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const save = () => {
    // Snapshotted: the dialog is gone by the time the write settles.
    const checklist = stepsFromDraft(rows);
    onClose();
    tasks.start<FieldSettings>({
      label: 'Save the job checklist',
      run: () => api.put<FieldSettings>('/field/settings', { checklist }),
      done: (r) =>
        `Checklist saved — ${r.checklist.length} step${r.checklist.length === 1 ? '' : 's'}, ${
          r.checklist.filter((s) => s.required).length
        } required.`,
      invalidate: [['field', 'settings'], ['field', 'jobs']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Job checklist steps</h2>
          <p className="mt-0.5 text-xs text-muted">
            In the order the technician works them. A light reading takes a dBm figure; a note takes
            a sentence. Relabelling a step keeps the ticks already made against it.
          </p>
        </header>

        <div className="max-h-[65vh] space-y-2 overflow-y-auto p-5">
          {rows.length === 0 && (
            <p className="rounded-lg border border-line bg-surface-2 px-3 py-4 text-center text-sm text-muted">
              No steps — the job will carry no checklist. Add one, or restore the built-in list.
            </p>
          )}
          {rows.map((row, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2">
              <span className="w-5 text-xs text-faint">{i + 1}.</span>
              <input
                className="input min-w-0 flex-1"
                value={row.label}
                maxLength={120}
                placeholder="What the technician does"
                onChange={(e) => setRows((list) => relabelDraftRow(list, i, e.target.value))}
                autoFocus={row.fresh && row.label === ''}
              />
              <select
                className="input w-44"
                value={row.kind}
                onChange={(e) => update(i, { kind: e.target.value as FieldChecklistKind })}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {CHECKLIST_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={row.required}
                  onChange={(e) => update(i, { required: e.target.checked })}
                />
                Required
              </label>
              <button
                type="button"
                className="text-muted hover:text-danger"
                aria-label={`Remove step ${i + 1}`}
                onClick={() => setRows((list) => list.filter((_, j) => j !== i))}
              >
                <Trash2 className="size-4" />
              </button>
              {row.key && <span className="w-full font-mono text-[11px] text-faint">{row.key}</span>}
            </div>
          ))}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setRows(addDraftRow)}>
              Add a step
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRows(draftRowsFrom(DEFAULT_FIELD_CHECKLIST))}>
              Restore the built-in list
            </Button>
          </div>
          {problem && <p className="text-xs text-warn">{problem}</p>}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" icon={<ArrowLeft className="size-4" />} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={problem !== null} onClick={save}>
            Save
          </Button>
        </footer>
      </div>
    </div>
  );
}
